import {HttpError} from '../../../network/errors/HttpError'
import {Nullable} from '../../../app/TypeHelpers'
import React, {ReactNode} from 'react'
import {HttpTimeoutError} from '../../../network/errors/baseHandling/HttpTimeoutError'
import {MaintenanceError} from '../../../network/errors/MaintenanceError'
import EventEmitterEx from '../pageBaseComponents/EventEmitterEx'
import {Messages} from '../../../Messages'

interface ExternalProps {
    eventEmitter: EventEmitterEx
}

export type AsyncFunction<A, R> = (args?: A[]) => Promise<R>
export type CallBackHttpErrorHandler = (httpError: HttpError | HttpTimeoutError) => void
export type AsyncCallFunctionType = <R, A>(
    asyncFunction: AsyncFunction<A, R>,
    httpErrorHandler: CallBackHttpErrorHandler,
    params?: {
        defaultErrorTitle?: string,
        defaultErrorTitlePriority?: boolean,
        replacementDetailErrorMessages?: ReplacementDetailErrorMessages[],
        retryTimes?: number
    }
) => Promise<Nullable<R>>

export interface AsyncCommunicationProps {
    asyncCallFunction: AsyncCallFunctionType,
}

export interface ReplacementDetailErrorMessages {
    targetMessageId: Messages
    replacementMessageId: Messages
}

export const withAsyncCommunication = <OriginalProps extends {}>(
    WrappedComponent: React.ComponentType<OriginalProps & AsyncCommunicationProps>,
) => {

    type ResultProps = OriginalProps & ExternalProps

    return class extends React.Component<ResultProps> {

        static displayName: string = `withAsyncCommunication(${WrappedComponent.displayName})`

        constructor(props: Readonly<OriginalProps & ExternalProps>) {
            super(props)
            this.callWithCommonErrorHandling = this.callWithCommonErrorHandling.bind(this)
        }

        async callWithCommonErrorHandling<R, A>(
            asyncFunction: AsyncFunction<A, R>,
            httpErrorHandler: CallBackHttpErrorHandler,
            params?: {
                defaultErrorTitle?: string
                defaultErrorTitlePriority? : boolean
                replacementDetailErrorMessages?: ReplacementDetailErrorMessages[],
                retryTimes?: number,
            },
        ): Promise<Nullable<R>> {
            let catchedException: any

            let execTimes = 1
            if (params && params.retryTimes) {
                execTimes = params.retryTimes + 1
            }

            for (let count: number = 0; count < execTimes; count++) {
                try {
                    return await asyncFunction()
                } catch (exception) {
                    if (exception instanceof MaintenanceError) {
                        if (window.name !== 'maintenance') {
                            window.location.reload()
                            window.name = 'maintenance'
                            return null
                        } else {
                            window.name = ''
                            return null
                        }
                    }
                    catchedException = exception
                }
            }
            if (catchedException) {
                const defaultErrorTitle = params && params.defaultErrorTitle
                const defaultErrorTitlePriority = params && params.defaultErrorTitlePriority
                const replacementDetailErrorMessages = params && params.replacementDetailErrorMessages
                this.doErrorHandlingExceptionType(
                    catchedException,
                    httpErrorHandler,
                    defaultErrorTitle,
                    defaultErrorTitlePriority,
                    replacementDetailErrorMessages
                )
            }
            return null
        }


        private overwriteDefaultErrorTitleToError(
            error: HttpError | HttpTimeoutError,
            defaultErrorTitle?: string,
            defaultErrorTitlePriority? : boolean,
            replacementDetailErrorMessages?: ReplacementDetailErrorMessages[],
        ): HttpError | HttpTimeoutError {
            if (!defaultErrorTitle) {
                return error
            }
            if (!defaultErrorTitlePriority && error.responseBody.requestError.title) {
                return error
            }
            error.responseBody.requestError.title = defaultErrorTitle
            error = this.replacementDetailErrorMessage(error, replacementDetailErrorMessages)
            return error
        }

        private replacementDetailErrorMessage(
            error: HttpError,
            replacementDetailErrorMessages?: ReplacementDetailErrorMessages[],
        ): HttpError {
            if (!replacementDetailErrorMessages) {
                return error
            }
            const replacementMessage = replacementDetailErrorMessages.find(replacement => {
                return replacement.targetMessageId.id.toString()
                    === error.responseBody.requestError.messageId
            })
            if (replacementMessage) {
                error.responseBody.requestError.message = replacementMessage.replacementMessageId.message
                error.responseBody.requestError.messageId = replacementMessage.replacementMessageId.id
            }
            return error
        }

        private doErrorHandlingExceptionType(
            exception: any,
            httpErrorHandler: CallBackHttpErrorHandler,
            defaultErrorTitle?: string,
            defaultErrorTitlePriority? : boolean,
            replacementDetailErrorMessages?: ReplacementDetailErrorMessages[]
        ): void {
            switch (exception.constructor) {
                case HttpError:
                    httpErrorHandler(this.overwriteDefaultErrorTitleToError(
                        exception,
                        defaultErrorTitle,
                        defaultErrorTitlePriority,
                        replacementDetailErrorMessages,
                    ))
                    break
                case HttpTimeoutError:
                    httpErrorHandler(this.overwriteDefaultErrorTitleToError(
                        exception,
                        defaultErrorTitle,
                    ))
                    break
                default:
                    this.props.eventEmitter.emit('systemErrorOccured', exception)
                    break
            }
        }

        render(): ReactNode {
            return (<>
                <WrappedComponent
                    {...this.props}
                    asyncCallFunction={this.callWithCommonErrorHandling}
                />
            </>)
        }
    }
}
